import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { AdventureSummary, Group, Instance, InstanceSummary, Person } from '@gm-binder/shared';
import { api, mediaUrl } from '../api/client';
import { AppShell } from '../components/layout/AppShell';
import {
  Card,
  Badge,
  Button,
  IconButton,
  MediaImage,
  Modal,
  LoadingState,
  EmptyState,
} from '../components/ui';
import { useUiStore } from '../store/uiStore';
import { groupAccent, initials } from '../lib/groups';
import { hasPhone, smsLink, whatsappLink } from '../lib/contact';
import { formatDate } from '../lib/datetime';
import { GroupModal } from './GroupsPage';
import styles from './GroupDetailPage.module.css';

function lastPlayed(inst: { sessionLog: { date: string }[] }): string | null {
  if (!inst.sessionLog.length) return null;
  return inst.sessionLog.map((e) => e.date).sort((a, b) => b.localeCompare(a))[0];
}

export function GroupDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useUiStore((s) => s.toast);

  const [group, setGroup] = useState<Group | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [people, setPeople] = useState<Person[]>([]);
  const [sessions, setSessions] = useState<Instance[]>([]);
  const [editing, setEditing] = useState(false);
  const [picking, setPicking] = useState(false);

  const reload = async () => {
    try {
      const g = await api.getGroup(id);
      const [ppl, summaries] = await Promise.all([api.listPeople(), api.listInstances()]);
      const mine = summaries.filter((s: InstanceSummary) => s.groupId === id);
      const full = await Promise.all(mine.map((s) => api.getInstance(s.id)));
      setGroup(g);
      setPeople(ppl);
      setSessions(full.filter((i): i is Instance => !!i));
    } catch {
      setNotFound(true);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const members = useMemo(() => {
    if (!group) return [];
    const byId = new Map(people.map((p) => [p.id, p] as const));
    return group.memberIds.map((mid) => byId.get(mid)).filter((p): p is Person => !!p);
  }, [group, people]);

  const saveGroup = async (input: Parameters<typeof api.updateGroup>[1]) => {
    if (!group) return;
    try {
      await api.updateGroup(group.id, input);
      toast('Gruppo aggiornato', 'success');
      setEditing(false);
      await reload();
    } catch {
      toast('Salvataggio fallito', 'danger');
    }
  };

  const removeGroup = async () => {
    if (!group) return;
    if (!confirm(`Eliminare il gruppo "${group.name}"? Le sessioni restano, ma perdono il legame.`))
      return;
    await api.deleteGroup(group.id);
    toast('Gruppo eliminato', 'info');
    navigate('/groups');
  };

  const startOneShot = async (adv: AdventureSummary) => {
    if (!group) return;
    try {
      const inst = await api.createInstance({
        adventureSlug: adv.slug,
        name: `${adv.title} — ${group.name}`,
        groupId: group.id,
      });
      navigate(`/run/${inst.id}`);
    } catch {
      toast('Avvio fallito', 'danger');
    }
  };

  if (notFound) {
    return (
      <AppShell>
        <EmptyState
          icon="⚠️"
          title="Gruppo non trovato"
          actions={
            <Link to="/groups">
              <Button variant="primary">Torna ai gruppi</Button>
            </Link>
          }
        />
      </AppShell>
    );
  }

  if (!group) {
    return (
      <AppShell>
        <LoadingState />
      </AppShell>
    );
  }

  const accent = { ['--accent' as string]: groupAccent(group.color) };
  const active = sessions.filter((s) => s.status !== 'completed');
  const past = sessions.filter((s) => s.status === 'completed');

  return (
    <AppShell>
      <Link to="/groups" className={styles.back}>
        ← Gruppi
      </Link>

      <header className={styles.head} style={accent}>
        <span className={styles.seal} aria-hidden />
        <div className={styles.headMain}>
          <h1 className={styles.title}>{group.name}</h1>
          <p className={styles.sub}>
            {members.length} {members.length === 1 ? 'membro' : 'membri'} ·{' '}
            {sessions.length} {sessions.length === 1 ? 'sessione' : 'sessioni'}
          </p>
          {group.notes && <p className={styles.notes}>{group.notes}</p>}
        </div>
        <div className={styles.headActions}>
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Modifica
          </Button>
          <IconButton label="Elimina gruppo" onClick={removeGroup}>
            🗑️
          </IconButton>
        </div>
      </header>

      {/* Members */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Membri</h2>
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
            Gestisci membri
          </Button>
        </div>
        {members.length === 0 ? (
          <EmptyState
            icon="🧑‍🤝‍🧑"
            title="Nessun membro"
            description="Aggiungi i giocatori dalla tua rubrica per averli pronti a ogni sessione."
            actions={
              <Button variant="primary" onClick={() => setEditing(true)}>
                Aggiungi membri
              </Button>
            }
          />
        ) : (
          <div className={styles.memberGrid}>
            {members.map((p) => (
              <Card key={p.id} padded={false}>
                <div className={styles.memberRow}>
                  <span className={styles.memberAvatar} aria-hidden>
                    {initials(p.name)}
                  </span>
                  <div className={styles.memberInfo}>
                    <div className={styles.memberName}>{p.name}</div>
                    <div className={styles.memberContacts}>
                      {hasPhone(p.phone) ? (
                        <>
                          <a
                            className={styles.contact}
                            href={whatsappLink(p.phone, `Ciao ${p.name}!`)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            WhatsApp
                          </a>
                          <a className={styles.contact} href={smsLink(p.phone, `Ciao ${p.name}!`)}>
                            SMS
                          </a>
                        </>
                      ) : (
                        <span className={styles.muted}>Nessun contatto</span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Sessions */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Sessioni</h2>
          <Button variant="primary" icon="🕯️" onClick={() => setPicking(true)}>
            Nuova one-shot
          </Button>
        </div>

        {sessions.length === 0 ? (
          <EmptyState
            icon="🎲"
            title="Nessuna sessione per questo gruppo"
            description="Avvia una one-shot dalla libreria: resterà legata al gruppo e ai suoi giocatori."
            actions={
              <Button variant="primary" onClick={() => setPicking(true)}>
                Avvia una one-shot
              </Button>
            }
          />
        ) : (
          <div className={styles.list}>
            {[...active, ...past].map((inst) => {
              const last = lastPlayed(inst);
              return (
                <Card key={inst.id} padded={false}>
                  <div className={styles.sessionRow}>
                    <MediaImage
                      src={mediaUrl(inst.adventureSlug, inst.adventureCover)}
                      alt=""
                      fallback="🕯️"
                      className={styles.cover}
                    />
                    <div className={styles.sessionMain}>
                      <div className={styles.sessionName}>{inst.name}</div>
                      <div className={styles.sessionMeta}>
                        <Badge
                          tone={
                            inst.status === 'active'
                              ? 'success'
                              : inst.status === 'paused'
                                ? 'warn'
                                : 'neutral'
                          }
                        >
                          {inst.status === 'active'
                            ? 'In corso'
                            : inst.status === 'paused'
                              ? 'In pausa'
                              : 'Conclusa'}
                        </Badge>
                        {last && <span>Ultima: {formatDate(last)}</span>}
                        <span>{inst.sessionLog.length} sessioni giocate</span>
                      </div>
                    </div>
                    <Link to={`/run/${inst.id}`}>
                      <Button size="sm" variant={inst.status === 'completed' ? 'ghost' : 'primary'}>
                        {inst.status === 'completed' ? 'Rivedi' : 'Riprendi'}
                      </Button>
                    </Link>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {editing && (
        <GroupModal
          group={group}
          people={people}
          onClose={() => setEditing(false)}
          onSave={saveGroup}
        />
      )}
      {picking && <AdventurePicker onPick={startOneShot} onClose={() => setPicking(false)} />}
    </AppShell>
  );
}

function AdventurePicker({
  onPick,
  onClose,
}: {
  onPick: (adv: AdventureSummary) => void | Promise<void>;
  onClose: () => void;
}) {
  const [adventures, setAdventures] = useState<AdventureSummary[] | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setAdventures(await api.listAdventures());
      } catch {
        setAdventures([]);
      }
    })();
  }, []);

  return (
    <Modal open onClose={onClose} title="Scegli un'avventura" width={560}>
      {adventures == null ? (
        <LoadingState />
      ) : adventures.length === 0 ? (
        <EmptyState
          title="Libreria vuota"
          description="Crea prima un'avventura nella libreria."
          actions={
            <Link to="/library">
              <Button variant="primary">Vai alla libreria</Button>
            </Link>
          }
        />
      ) : (
        <div className={styles.pickGrid}>
          {adventures.map((a) => (
            <button key={a.slug} type="button" className={styles.pick} onClick={() => void onPick(a)}>
              <MediaImage
                src={mediaUrl(a.slug, a.cover)}
                alt=""
                fallback="🕯️"
                className={styles.pickCover}
              />
              <span className={styles.pickTitle}>{a.title}</span>
              {a.subtitle && <span className={styles.pickSub}>{a.subtitle}</span>}
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}
